import React, { useEffect, useRef, useState } from 'react';
import { Scanner } from '../components/Scanner';
import { ProductCard } from '../components/ProductCard';
import { LoadMoreButton } from '../components/LoadMoreButton';
import { History } from 'lucide-react';
import { useStore } from '../store';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import type { ScanHistoryItem } from '../types';

const ITEMS_PER_PAGE = 5;

export const ScannerPage: React.FC = () => {
  const { user } = useAuth();
  const scanHistory = useStore((state) => state.scanHistory);
  const setScanHistory = useStore((state) => state.setScanHistory);
  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  const [loading, setLoading] = useState(false);
  const scannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadScanHistory() {
      if (!user) {
        setScanHistory([]);
        return;
      }

      try {
        const { data: history, error } = await supabase
          .from('scan_history')
          .select(`
            scanned_at,
            products (
              id,
              barcode,
              name,
              brand,
              status,
              certified_by,
              ingredients,
              image_url,
              created_at,
              updated_at
            )
          `)
          .eq('user_id', user.id)
          .order('scanned_at', { ascending: false });

        if (error) throw error;

        const formattedHistory: ScanHistoryItem[] = history.map(item => ({
          scannedAt: new Date(item.scanned_at),
          product: {
            id: item.products.id,
            barcode: item.products.barcode,
            name: item.products.name,
            brand: item.products.brand,
            status: item.products.status,
            certifiedBy: item.products.certified_by,
            ingredients: item.products.ingredients,
            imageUrl: item.products.image_url,
            createdAt: item.products.created_at,
            updatedAt: item.products.updated_at
          }
        }));

        setScanHistory(formattedHistory);
      } catch (error) {
        console.error('Error loading scan history:', error);
      }
    }

    loadScanHistory();
  }, [user, setScanHistory]);

  const handleLoadMore = () => {
    setLoading(true);
    setTimeout(() => {
      setVisibleItems(prev => prev + ITEMS_PER_PAGE);
      setLoading(false);
    }, 500); // Add small delay for better UX
  };

  return (
    <main className="max-w-4xl mx-auto">
      <section 
        ref={scannerRef} 
        className="fixed top-[57px] left-0 right-0 z-20 bg-gray-50 px-3 sm:px-4 py-3 border-b border-gray-100"
        style={{
          maxWidth: '896px', // 4xl in pixels
          margin: '0 auto',
        }}
      >
        <Scanner />
        {scanHistory.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center gap-2 text-gray-900">
              <History className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
              <h2 className="text-lg sm:text-xl font-semibold">Your Scan History</h2>
            </div>
            {!user && (
              <p className="mt-1 text-sm text-gray-600">
                Sign up to save your scan history and access it across devices
              </p>
            )}
          </div>
        )}
      </section>
      
      <section className="pt-[150px] px-3 sm:px-4 pb-4 sm:pb-8">
        <div className="space-y-3 sm:space-y-4">
          {scanHistory.length === 0 ? (
            <p className="text-center text-gray-500 py-6 sm:py-8">
              🔍 Scan a product to verify Halal status
            </p>
          ) : (
            scanHistory.slice(0, visibleItems).map((item, index) => (
              <ProductCard
                key={`${item.product.barcode}-${index}`}
                product={item.product}
                showTimestamp
                timestamp={item.scannedAt}
              />
            ))
          )}
        </div>
        {scanHistory.length > visibleItems && (
          <div className="mt-4">
            <LoadMoreButton
              onClick={handleLoadMore}
              loading={loading}
            />
          </div>
        )}
      </section>
    </main>
  );
};